import { useEffect, useState } from "react";

interface HarnessResult {
  generated_at: string;
  worker_url: string;
  fixture_count: number;
  pass_count: number;
  pass_rate: number;
  point_match_threshold_pct: number;
  fixture_pass_precision_threshold?: number;
  fixture_pass_coverage_threshold?: number;
  fixture_pass_threshold?: number;
  per_fixture: {
    name: string;
    chart_type: string;
    points_total: number;
    in_range_total?: number;
    points_matched: number;
    point_match_rate: number;
    precision?: number;
    coverage?: number;
    pass: boolean;
    notes?: string;
  }[];
  ab_baseline?: {
    model: string;
    pass_rate: number;
    delta_pp: number;
  } | null;
}

export function Accuracy() {
  const [data, setData] = useState<HarnessResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/harness-results.json")
      .then((r) => {
        if (!r.ok) throw new Error("results not yet published");
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">Accuracy harness</h1>
        <p className="text-ink/70 max-w-2xl">
          We score every deploy against a fixture set with known ground-truth
          CSVs — 20 synthetic charts (matplotlib) plus curated real-world
          charts from Our World in Data, the St Louis Fed (FRED), and
          Wikimedia Commons. Two metrics per fixture:{" "}
          <strong>precision</strong> (of points where the model covered the
          chart's range, fraction within ±5% tolerance) and{" "}
          <strong>coverage</strong> (fraction of truth points that the
          extraction's range covered). A fixture passes if precision ≥ 80%
          AND coverage ≥ 50%. The whole harness lives at{" "}
          <a
            className="underline"
            href="https://github.com/estowe10/chart-screenshot-to-csv/tree/main/harness"
          >
            harness/
          </a>
          .
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-900">
          Harness results not published yet. Run{" "}
          <code className="font-mono">npm run harness</code> on a deployed
          Worker and copy <code className="font-mono">harness/results.json</code>{" "}
          into <code className="font-mono">frontend/public/</code>.
        </div>
      )}

      {data && (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <Stat label="Pass rate" value={`${(data.pass_rate * 100).toFixed(1)}%`} />
            <Stat
              label="Fixtures passed"
              value={`${data.pass_count} / ${data.fixture_count}`}
            />
            <Stat
              label="Point tolerance"
              value={`±${data.point_match_threshold_pct}%`}
            />
          </div>
          {data.ab_baseline && (
            <div className="rounded-md border border-ink/15 p-4 text-sm">
              <strong>A/B baseline:</strong> {data.ab_baseline.model} scored{" "}
              {(data.ab_baseline.pass_rate * 100).toFixed(1)}% on the same
              fixtures — Opus 4.7 delta:{" "}
              <span
                className={
                  data.ab_baseline.delta_pp >= 0 ? "text-green-700" : "text-red-700"
                }
              >
                {data.ab_baseline.delta_pp >= 0 ? "+" : ""}
                {data.ab_baseline.delta_pp.toFixed(1)} pp
              </span>
              .
            </div>
          )}
          <div className="overflow-x-auto border border-ink/10 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-ink/5">
                <tr>
                  <th className="text-left px-3 py-2">Fixture</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-right px-3 py-2">Points</th>
                  <th className="text-right px-3 py-2">Precision</th>
                  <th className="text-right px-3 py-2">Coverage</th>
                  <th className="text-right px-3 py-2">Pass</th>
                </tr>
              </thead>
              <tbody>
                {data.per_fixture.map((f) => {
                  const prec = f.precision ?? f.point_match_rate;
                  const cov =
                    f.coverage ??
                    (f.points_total > 0 && f.in_range_total != null
                      ? f.in_range_total / f.points_total
                      : 1);
                  return (
                    <tr key={f.name} className="border-t border-ink/10">
                      <td className="px-3 py-1.5 font-mono">{f.name}</td>
                      <td className="px-3 py-1.5">{f.chart_type}</td>
                      <td className="px-3 py-1.5 text-right">{f.points_total}</td>
                      <td className="px-3 py-1.5 text-right">
                        {(prec * 100).toFixed(0)}%
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {(cov * 100).toFixed(0)}%
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {f.pass ? (
                          <span className="text-green-700 font-medium">pass</span>
                        ) : (
                          <span className="text-red-700 font-medium">fail</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-ink/50">
            Generated at {data.generated_at} from {data.worker_url}.
          </p>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-ink/10 p-4">
      <div className="text-xs uppercase tracking-wide text-ink/60">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
